import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Check } from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";

/**
 * Save-to-Board bottom sheet.
 *
 * Behaviour:
 *  - Lists the caller's boards with cover thumbnails + pin counts.
 *  - Shows which boards already contain this pin (checkmark).
 *  - "+ Create New Board" inline creates + auto-saves in one round trip.
 *  - Idempotent — hitting save twice on the same board is a no-op server-side.
 */
export default function SaveToBoardDialog({ pin, open, onOpenChange }) {
  const [boards, setBoards] = useState([]);
  const [savedBoardIds, setSavedBoardIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBoardName, setNewBoardName] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);

  useEffect(() => {
    if (!open || !pin) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/blinterest/boards`, { withCredentials: true }),
      axios.get(`${API}/blinterest/pins/${pin.pin_id}/save-state`, { withCredentials: true }),
    ])
      .then(([boardsRes, stateRes]) => {
        setBoards(boardsRes.data.boards || []);
        setSavedBoardIds(new Set(stateRes.data.board_ids || []));
      })
      .catch(() => {
        toast.error("Couldn't load your Collections");
      })
      .finally(() => setLoading(false));
  }, [open, pin]);

  const saveToBoard = async (board) => {
    if (!pin) return;
    const already = savedBoardIds.has(board.board_id);
    try {
      if (already) {
        await axios.delete(
          `${API}/blinterest/boards/${board.board_id}/pins/${pin.pin_id}`,
          { withCredentials: true },
        );
        setSavedBoardIds((prev) => {
          const next = new Set(prev);
          next.delete(board.board_id);
          return next;
        });
        toast.success(`Removed from ${board.name}`);
      } else {
        await axios.post(
          `${API}/blinterest/boards/${board.board_id}/pins/${pin.pin_id}`,
          {},
          { withCredentials: true },
        );
        setSavedBoardIds((prev) => new Set(prev).add(board.board_id));
        toast.success(`Great Find! Added to ${board.name} ✓`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update your Collection");
    }
  };

  const createAndSave = async () => {
    if (!pin) return;
    const name = newBoardName.trim();
    if (!name) {
      toast.error("Give your Collection a name first");
      return;
    }
    setCreating(true);
    try {
      const boardRes = await axios.post(
        `${API}/blinterest/boards`,
        { name, privacy: "public" },
        { withCredentials: true },
      );
      const board = boardRes.data;
      await axios.post(
        `${API}/blinterest/boards/${board.board_id}/pins/${pin.pin_id}`,
        {},
        { withCredentials: true },
      );
      setBoards((prev) => [board, ...prev]);
      setSavedBoardIds((prev) => new Set(prev).add(board.board_id));
      setNewBoardName("");
      setShowNewForm(false);
      toast.success(`Great Find! Added to ${board.name} ✓`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create Collection");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-tbc-navy text-white border-white/10 sm:max-w-md"
        data-testid="save-to-board-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-white">Keep This Find</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3 py-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="max-h-[50vh] overflow-y-auto space-y-2 py-2">
              {boards.length === 0 ? (
                <p className="text-white/60 text-sm font-body py-2">
                  You don&apos;t have any Collections yet. Start your first one below.
                </p>
              ) : (
                boards.map((b) => {
                  const saved = savedBoardIds.has(b.board_id);
                  return (
                    <button
                      key={b.board_id}
                      type="button"
                      onClick={() => saveToBoard(b)}
                      className={`w-full flex items-center gap-3 p-2 rounded-xl border transition-colors text-left ${
                        saved
                          ? "border-tbc-gold/50 bg-tbc-gold/10"
                          : "border-white/10 hover:border-white/30"
                      }`}
                      data-testid={`save-to-board-${b.board_id}`}
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 flex-none">
                        {b.cover_image ? (
                          <img
                            src={resolveAssetUrl(b.cover_image)}
                            alt={b.name}
                            className="w-full h-full object-cover"
                          />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-sm text-white truncate">{b.name}</p>
                        <p className="text-xs text-white/50">
                          {b.pin_count || 0} Find{b.pin_count === 1 ? "" : "s"}
                        </p>
                      </div>
                      {saved && <Check className="w-4 h-4 text-tbc-gold flex-none" />}
                    </button>
                  );
                })
              )}
            </div>

            {showNewForm ? (
              <div className="flex gap-2 pt-2">
                <Input
                  value={newBoardName}
                  onChange={(e) => setNewBoardName(e.target.value)}
                  placeholder="Collection name"
                  className="bg-white/10 border-white/20 text-white h-11"
                  data-testid="new-board-name-input"
                />
                <Button
                  onClick={createAndSave}
                  disabled={creating}
                  className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy h-11 rounded-full px-6"
                  data-testid="new-board-save-btn"
                >
                  {creating ? "Keeping…" : "Keep"}
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setShowNewForm(true)}
                className="w-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy h-12 rounded-full font-heading"
                data-testid="new-board-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Start a New Collection
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
